import React from 'react'
import { Divider, Stack, Typography } from '@mui/material'

import { Button, Input } from '../../../../interaxDesignSystemComponents'
import { INTERAX_COLORS } from '../../../../constants/colors'
import { TESTIDS } from '../../../../config/testids'
import { useTranslation } from '../../../../shared/hooks/useIntractionTranslate'
import { LeaderScopePeopleList } from './LeaderScopePeopleList'
import { LeaderScopeQuickAdd } from './LeaderScopeQuickAdd'
import { leaderScopeBodyTextSx, leaderScopeErrorTextSx, leaderScopeMutedTextSx } from './leaderScopeStyles'
import type { useLeaderScopeSelection } from './useLeaderScopeSelection'

export type LeaderScopeEditorProps = {
  selection: ReturnType<typeof useLeaderScopeSelection>
}

/** The body of the modal once the saved list has loaded: quick-add, search, the tick list and what is wrong with it. */
export const LeaderScopeEditor: React.FC<LeaderScopeEditorProps> = ({ selection }) => {
  const { t } = useTranslation()
  const hasQuickAddGroups = selection.teamGroups.length > 0 || selection.sprintGroups.length > 0
  const saveErrorKey = selection.errorTranslationKey
  const saveErrorText = saveErrorKey ? t(saveErrorKey, { max: selection.maxUsers }) : ''
  const hasSaveError = !!saveErrorText

  return (
    <>
      {hasQuickAddGroups && (
        <>
          <Stack spacing={1}>
            <LeaderScopeQuickAdd
              labelKey="org.leaderScope.addTeam"
              groups={selection.teamGroups}
              selectedUserIds={selection.selectedUserIds}
              onAdd={selection.addPeople}
            />
            <LeaderScopeQuickAdd
              labelKey="org.leaderScope.addSprint"
              groups={selection.sprintGroups}
              selectedUserIds={selection.selectedUserIds}
              onAdd={selection.addPeople}
            />
          </Stack>

          <Divider sx={{ borderColor: INTERAX_COLORS.white10 }} />
        </>
      )}

      <Input
        autoFocus
        fullWidth
        value={selection.searchText}
        onChange={(event) => selection.setSearchText(event.target.value)}
        placeholder={t('org.leaderScope.searchPlaceholder')}
        data-testid={TESTIDS.LEADER_SCOPE_SEARCH}
      />

      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
        <Typography sx={leaderScopeBodyTextSx}>
          {t('org.leaderScope.selectedCount', { count: selection.selectedUserIds.length })}
        </Typography>
        <Button variant="secondary" size="small" onClick={selection.selectAllShown}>
          {t('org.leaderScope.selectAllShown')}
        </Button>
        <Button variant="secondary" size="small" onClick={selection.clearSelection}>
          {t('org.leaderScope.clear')}
        </Button>
      </Stack>

      {!selection.hasEligibleMembers && (
        <Typography sx={leaderScopeMutedTextSx}>{t('org.leaderScope.empty')}</Typography>
      )}

      {selection.hasEligibleMembers && (
        <LeaderScopePeopleList
          members={selection.shownMembers}
          selectedUserIds={selection.selectedUserIds}
          onToggle={selection.togglePerson}
        />
      )}

      {selection.isOverTheCap && (
        <Typography sx={leaderScopeErrorTextSx}>{t('org.leaderScope.tooMany', { max: selection.maxUsers })}</Typography>
      )}

      {hasSaveError && <Typography sx={leaderScopeErrorTextSx}>{saveErrorText}</Typography>}
    </>
  )
}
